// SB6-ANA-FILT-05 (D6) — collapse state for the Global Data Point
// Filter widget. Persisted in localStorage, per-browser global (NOT per
// data-acquisition); opening a new data set never auto-collapses.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "lyse.globalDataPointFilter.collapsed";

function read(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function write(collapsed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    // localStorage unavailable (private mode / quota) — collapse state
    // simply won't persist; the in-memory value still drives the UI.
  }
}

export interface UseFilterCollapseResult {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggle: () => void;
}

export function useFilterCollapse(): UseFilterCollapseResult {
  const [collapsed, setCollapsedState] = useState<boolean>(read);

  // Re-read on mount so SSR/hydration mismatches don't strand a stale
  // initial value (read() returns false on the server).
  useEffect(() => {
    setCollapsedState(read());
  }, []);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    write(next);
  }, []);

  const toggle = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      write(next);
      return next;
    });
  }, []);

  return { collapsed, setCollapsed, toggle };
}
